/**
 * ResultRow — one row in the search results.
 *
 * Layout (Stripe / Vercel / Raycast pattern — breadcrumb chain INLINE
 * with the title, leaf last, left-to-right):
 *
 *   ┌────┬───────────────────────────────────────┬──────────┐
 *   │ ⬚  │ Crumb › Crumb › TitleLeaf             │ ⌘K  →   │
 *   │    │ optional subtitle (small)              │          │
 *   └────┴───────────────────────────────────────┴──────────┘
 *
 *  • Icon tile on the LEFT (28px square).
 *  • Title block IMMEDIATELY after icon: ancestors first, separated by
 *    ChevronRight, then the leaf (bold + highlighted match positions).
 *  • Optional subtitle below.
 *  • Shortcut hint OR a chevron-right (drill arrow) pinned right.
 *  • Selected state: 3px primary left-edge marker, accent wash, the glow.
 *
 * Matches the screenshots the user provided (Domains › Overview,
 * Settings › Team and security) — the chain reads as one inline path.
 */
"use client";

import { Fragment } from "react";
import { CaretRight } from "@phosphor-icons/react";
import { CommandItem } from "@/shared/ui/command";
import { cn } from "@/shared/lib/utils";
import type { PaletteCtx, Result } from "../types";
import { ResultHighlight } from "./ResultHighlight";
import { KbdHint, parseShortcut } from "./KbdHint";
import { recordSelection } from "../search/frecency";

interface ResultRowProps {
  result: Result;
  ctx: PaletteCtx;
  showBreadcrumbs?: boolean;
}

export function ResultRow({ result, ctx, showBreadcrumbs = true }: ResultRowProps) {
  const Icon = result.icon;
  const hasChevron = (result.verbs?.length ?? 0) > 0;
  const inlineCrumbs = showBreadcrumbs ? result.scopeChain : [];
  const handleSelect = () => {
    recordSelection(result.id);
    result.onSelect(ctx);
  };

  return (
    <CommandItem
      value={`${result.id} ${result.title}`}
      onSelect={handleSelect}
      className={cn(
        "group relative flex cursor-pointer items-center gap-3 rounded-md px-3 py-2",
        "transition-[background-color,box-shadow] duration-(--dur-fast)",
        "data-[selected=true]:bg-accent data-[selected=true]:shadow-ring",
        "before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2",
        "before:h-5 before:w-[3px] before:rounded-r-full before:bg-primary",
        "before:opacity-0 before:transition-opacity",
        "data-[selected=true]:before:opacity-100",
      )}
    >
      {Icon && (
        <div
          className={cn(
            "flex size-7 shrink-0 items-center justify-center rounded-md border border-border transition-[background-color,color] duration-(--dur-fast)",
            "bg-muted text-muted-foreground",
            "group-data-[selected=true]:bg-primary/10",
            "group-data-[selected=true]:text-primary",
          )}
        >
          <Icon className="size-4" />
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-center gap-1 text-[13.5px] leading-tight">
          {inlineCrumbs.map((node) => (
            <Fragment key={node.id}>
              <span className="shrink-0 truncate text-muted-foreground/80">
                {node.shortTitle ?? node.title}
              </span>
              <CaretRight className="size-3 shrink-0 opacity-40" />
            </Fragment>
          ))}
          <ResultHighlight
            text={result.title}
            positions={result.positions}
            className="truncate font-medium text-foreground"
          />
        </div>
        {result.subtitle && (
          <div className="mt-0.5 truncate text-[11.5px] text-muted-foreground">
            {result.subtitle}
          </div>
        )}
      </div>

      {result.shortcut && (
        <KbdHint keys={parseShortcut(result.shortcut)} className="shrink-0" />
      )}
      {hasChevron && (
        <CaretRight
          className="size-4 shrink-0 text-muted-foreground/50 transition-tint group-data-[selected=true]:text-primary/70"
        />
      )}
    </CommandItem>
  );
}
